import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { CalendarDays, Users, PartyPopper, TrendingUp } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, icon: Icon }) {
    return (
        <div className="border border-dorado/20 bg-carbon-suave/40 p-6">
            <div className="flex items-center gap-3 mb-2">
                <Icon size={16} className="text-dorado" />
                <p className="font-body uppercase tracking-[0.25em] text-[0.65rem] text-piedra">
                    {label}
                </p>
            </div>
            <p className="font-heading text-5xl text-crema font-light">{value}</p>
        </div>
    );
}

export default function CrmDashboard() {
    const [stats, setStats] = useState({ hoy: 0, semana: 0, mes: 0, total: 0 });
    const [proximas, setProximas] = useState([]);

    useEffect(() => {
        Promise.all([api.get("/reservas/stats"), api.get("/reservas/proximas")])
            .then(([s, p]) => {
                setStats(s.data);
                setProximas(p.data);
            })
            .catch((err) => toast.error(formatApiError(err)));
    }, []);

    return (
        <div className="space-y-12">
            <header>
                <p className="font-body uppercase tracking-[0.3em] text-xs text-dorado mb-3">
                    Dashboard
                </p>
                <h1 className="font-heading text-5xl text-crema font-light">
                    Hoy en <em className="italic text-dorado">Tenería</em>
                </h1>
            </header>

            <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="crm-stats">
                <Stat label="Hoy" value={stats.hoy} icon={CalendarDays} />
                <Stat label="Esta semana" value={stats.semana} icon={TrendingUp} />
                <Stat label="Este mes" value={stats.mes} icon={Users} />
                <Stat label="Total" value={stats.total} icon={PartyPopper} />
            </section>

            <section>
                <h2 className="font-heading text-3xl text-crema mb-6">
                    Próximas reservas{" "}
                    <span className="text-piedra text-base font-body">(48h)</span>
                </h2>
                {proximas.length === 0 ? (
                    <p className="font-body text-piedra italic">
                        No hay reservas en las próximas 48 horas.
                    </p>
                ) : (
                    <div className="border border-dorado/20 bg-carbon-suave/40 divide-y divide-dorado/10">
                        {proximas.map((r) => (
                            <div
                                key={r.id}
                                className="p-5 grid md:grid-cols-5 gap-3 items-center"
                                data-testid="crm-proxima-reserva-row"
                            >
                                <div>
                                    <p className="font-heading text-xl text-crema">{r.nombre}</p>
                                    <p className="font-body text-xs text-piedra">
                                        {r.email || r.telefono || "Sin contacto"}
                                    </p>
                                </div>
                                <div className="font-body text-sm text-crema">{r.fecha}</div>
                                <div className="font-heading italic text-dorado text-xl">
                                    {r.hora}
                                </div>
                                <div className="font-body text-sm text-piedra">
                                    {r.personas} pax
                                </div>
                                <div>
                                    <span
                                        className={`font-body text-[0.65rem] uppercase tracking-[0.2em] px-3 py-1 border ${
                                            r.estado === "confirmada"
                                                ? "border-dorado text-dorado"
                                                : r.estado === "cancelada"
                                                ? "border-red-400/50 text-red-400"
                                                : "border-piedra/40 text-piedra"
                                        }`}
                                    >
                                        {r.estado}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section className="grid md:grid-cols-3 gap-6">
                {[
                    { to: "/crm/reservas", t: "Reservas", d: "Gestiona reservas y confirma mesas." },
                    { to: "/crm/clientes", t: "Clientes", d: "Consulta el histórico de clientes." },
                    { to: "/crm/eventos", t: "Eventos", d: "Bodas, comuniones y eventos privados." },
                ].map((c) => (
                    <Link
                        key={c.to}
                        to={c.to}
                        className="border border-dorado/20 p-8 hover:border-dorado transition-colors block bg-carbon"
                    >
                        <h3 className="font-heading text-2xl text-dorado mb-2">{c.t}</h3>
                        <p className="font-body text-piedra text-sm">{c.d}</p>
                    </Link>
                ))}
            </section>
        </div>
    );
}
